import { styled } from 'styled-components';
import React, {
  CSSProperties,
  FC,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesUp } from '@fortawesome/free-solid-svg-icons/faAnglesUp';
import { faAnglesDown } from '@fortawesome/free-solid-svg-icons/faAnglesDown';
import { useWindowSize } from '@/hooks/useWindowSize';

const Container = styled.div`
  position: relative;
  overflow-y: hidden;
  word-break: break-word;
`;

const Content = styled.div``;

const Overlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  background: linear-gradient(180deg, transparent 40%, white calc(100% - 20px));
`;

const Bar = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  width: 100%;
  height: 20px;
  bottom: 0;
  border: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.grayscale.white};
  background: ${({ theme }) => theme.palette.grayscale.light3};

  &:hover {
    background: ${({ theme }) => theme.palette.grayscale.light2};
  }

  &:active {
    background: ${({ theme }) => theme.palette.grayscale.light3};
  }
`;

export type ExpandableProps = PropsWithChildren<{
  className?: string;
  style?: CSSProperties;
  maxHeight?: number | string;
}>;

export const Expandable: FC<ExpandableProps> = ({
  className,
  style,
  maxHeight,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [containerElement, setContainerElement] =
    useState<HTMLDivElement | null>(null);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [overflows, setOverflows] = useState(false);
  const windowSize = useWindowSize();

  useEffect(() => {
    if (containerElement && contentElement) {
      setOverflows(containerElement.clientHeight < contentElement.clientHeight);
    }
  }, [windowSize, containerElement, contentElement]);

  useEffect(() => {
    if (!overflows) {
      setOpen(false);
    }
  }, [overflows]);

  const toggleOpen = () => setOpen((open) => !open);

  return (
    <Container
      ref={(ref) => setContainerElement(ref)}
      className={className}
      style={{
        ...style,
        paddingBottom: overflows ? '24px' : undefined,
        maxHeight: open ? undefined : maxHeight,
      }}
    >
      <Content ref={(ref) => setContentElement(ref)}>{children}</Content>
      {overflows && (
        <>
          {!open && <Overlay />}
          <Bar onClick={toggleOpen} title="">
            <FontAwesomeIcon
              icon={open ? faAnglesUp : faAnglesDown}
              size="sm"
            />
          </Bar>
        </>
      )}
    </Container>
  );
};
